import { Face } from './Face';
import { Vertex } from './Vertex';

export type VertexAttribute = 'position' | 'normal' | 'color' | 'texture';

export type AttributeLayout = { offset: number; length: number };

export type CompiledGeometry = {
  data: Float32Array;
  vertexCount: number;
  vertexStride: number;
  layout: Partial<Record<VertexAttribute, AttributeLayout>>;
};

const ATTRIBUTE_LENGTH: Record<VertexAttribute, number> = {
  position: 3,
  normal: 3,
  color: 4,
  texture: 2,
};

// A bag of vertices and faces that can be packed into an interleaved
// Float32Array for upload to a GPU buffer. The set of attributes to include
// (and their order in the buffer) is chosen at compile() time.
export class Collection {
  public faces: Face[] = [];
  public vertices: Vertex[] = [];

  public addVertex(vertex: Vertex): Collection {
    this.vertices.push(vertex);
    return this;
  }

  public addVertices(vertices: Vertex[]): Collection {
    for (const v of vertices) this.vertices.push(v);
    return this;
  }

  public addFace(face: Face): Collection {
    this.faces.push(face);
    return this;
  }

  public addFaces(faces: Face[]): Collection {
    for (const f of faces) this.faces.push(f);
    return this;
  }

  public compile(attributes: VertexAttribute[]): CompiledGeometry {
    const layout: Partial<Record<VertexAttribute, AttributeLayout>> = {};
    let vertexStride = 0;
    for (const attr of attributes) {
      const length = ATTRIBUTE_LENGTH[attr];
      layout[attr] = { offset: vertexStride, length };
      vertexStride += length;
    }

    let vertexCount = 0;
    for (const face of this.faces) {
      vertexCount += face.vertices.length;
    }

    const data = new Float32Array(vertexCount * vertexStride);
    let offset = 0;
    for (const face of this.faces) {
      for (const vertex of face.vertices) {
        for (const attr of attributes) {
          const src = readAttribute(attr, face, vertex);
          data.set(src, offset);
          offset += ATTRIBUTE_LENGTH[attr];
        }
      }
    }

    return { data, vertexCount, vertexStride, layout };
  }
}

function readAttribute(attribute: VertexAttribute, face: Face, vertex: Vertex): ArrayLike<number> {
  switch (attribute) {
    case 'position':
      return vertex.position;
    case 'normal':
      return vertex.normal;
    case 'color':
      return vertex.color;
    case 'texture': {
      const offset = face.getTextureOffset(vertex);
      if (!offset) {
        throw new Error('Face is missing a texture offset for one of its vertices');
      }
      return offset;
    }
  }
}
